import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["toggle", "nav"];

  connect() {
    this._activeId = null;
    this._ticking = false;
    this._boundOnScroll = this._onScroll.bind(this);
    this._boundOnKey = this._onKey.bind(this);

    window.addEventListener("scroll", this._boundOnScroll, { passive: true });
    window.addEventListener("resize", this._boundOnResize.bind(this), { passive: true });
    window.addEventListener("keydown", this._boundOnKey);

    this._tagMethodLinks();
    this._sidebarIds = this._collectSidebarIds();

    requestAnimationFrame(() => {
      const hash = window.location.hash.slice(1).split("?")[0].trim();
      if (hash && document.getElementById(hash)) {
        this._activeId = hash;
        this._highlightSidebarFor(hash);
      } else {
        this._updateActiveSection();
      }
    });
  }

  disconnect() {
    window.removeEventListener("scroll", this._boundOnScroll);
    window.removeEventListener("keydown", this._boundOnKey);
    this._setOpen(false);
  }

  // ── Public actions ────────────────────────────────────────────────────────

  toggle() {
    const isOpen = this.element.classList.contains("sidebar-open");
    this._setOpen(!isOpen);
  }

  close() {
    this._setOpen(false);
  }

  // Close drawer when a nav link is clicked on mobile
  navLinkClick(event) {
    if (this._isMobile() && event.target.closest("a")) {
      this._setOpen(false);
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  _isMobile() {
    return window.innerWidth < 768;
  }

  _setOpen(open) {
    this.element.classList.toggle("sidebar-open", open);
    document.body.classList.toggle("sidebar-open", open);
  }

  _onKey(e) {
    if (e.key === "Escape" && this.element.classList.contains("sidebar-open")) {
      this._setOpen(false);
    }
  }

  _boundOnResize() {
    // Auto-close drawer if viewport grows past mobile breakpoint
    if (!this._isMobile()) {
      this._setOpen(false);
    }
    this._onScroll();
  }

  _tagMethodLinks() {
    if (!this.hasNavTarget) return;
    const methods = ["DELETE", "PATCH", "POST", "PUT", "GET"];
    this.navTarget.querySelectorAll("a.docs-sidebar-link").forEach((link) => {
      const text = link.textContent.trim();
      for (const method of methods) {
        if (text.startsWith(method + " ")) {
          const label = text.slice(method.length + 1);
          link.innerHTML = `<span class="cs-http-badge cs-http-badge--${method.toLowerCase()}">${method}</span><span class="cs-http-label">${label}</span>`;
          break;
        }
      }
    });
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._updateActiveSection();
      this._ticking = false;
    });
  }

  // Collect the set of anchor ids that actually appear in the sidebar, so the
  // scroll-spy only ever tracks sections a sidebar link can highlight.
  _collectSidebarIds() {
    const ids = new Set();
    if (!this.hasNavTarget) return ids;
    this.navTarget.querySelectorAll("a.docs-sidebar-link").forEach((link) => {
      const id = this._getLinkId(link.getAttribute("href") || "");
      if (id) ids.add(id);
    });
    return ids;
  }

  _getSections() {
    const inner = document.querySelector(".docs-content-inner");
    if (!inner) return [];
    const ids = this._sidebarIds || new Set();
    // Only consider sections that have a matching sidebar link. In-content
    // sub-headings (e.g. anchors from the "On this page" rail) have no sidebar
    // entry; including them let the active section advance to an id no link
    // maps to, which left the previous item stuck highlighted and the real one
    // never lit up.
    return Array.from(inner.querySelectorAll("[id]")).filter(
      (el) => el.id && el.offsetParent !== null && ids.has(el.id)
    );
  }

  _updateActiveSection() {
    const sections = this._getSections();
    if (!sections.length) return;

    const offset = 120;
    let active = sections[0];
    for (const el of sections) {
      if (el.getBoundingClientRect().top <= offset) {
        active = el;
      }
    }

    const id = active.id;
    if (!id || id === this._activeId) return;
    this._activeId = id;
    this._highlightSidebarFor(id);
  }

  _getLinkId(href) {
    if (!href) return "";
    const hash = href.indexOf("#");
    if (hash === -1) return "";
    return href.slice(hash + 1).split("?")[0].replace(/\/$/, "").trim();
  }

  _highlightSidebarFor(id) {
    if (!this.hasNavTarget) return;

    const links = this.navTarget.querySelectorAll("a.docs-sidebar-link");
    const matches = Array.from(links).filter(
      (link) => this._getLinkId(link.getAttribute("href") || "") === id
    );

    // No sidebar link maps to this id (e.g. an in-content sub-heading anchor
    // generated by the "On this page" rail) — keep the current highlight rather
    // than clearing it. Accordion open/close state is owned by AccordionController.
    if (!matches.length) return;

    links.forEach((link) => {
      link.classList.remove("docs-sidebar-link-active");
      link.removeAttribute("aria-current");
    });
    matches.forEach((link) => {
      link.classList.add("docs-sidebar-link-active");
      link.setAttribute("aria-current", "true");
    });
  }
}
